import { useState, useMemo } from 'react';
import {
  TrendingUp,
  Building2,
  ExternalLink,
  BarChart2
} from 'lucide-react';
import { usePromotionQuery } from '@/client/api/promotion-management/promotion.query';
import { Promotion } from '@/client/types/promotion';
import Pagination from '@/client/components/common/Pagination';
import { useListFilters } from '@/client/hooks/useListFilters';
import { getPromotionStatusFilter } from '@/client/utils/filters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Skeleton } from '@/components/ui/skeleton';
import ServiceRequestListFilters from '@/client/components/admin/ServoceRequest/ServiceRequestListFilters';
import ServiceRequestDetailsDrawer from '@/client/components/admin/ServoceRequest/ServiceRequestDetailsDrawer';
import { useServiceRequestsQuery } from '@/client/api/service-requests/service-requests.query';

const getProgressColor = (value: number, target: number) => {
  if (!target) return 'bg-gray-200';
  const percentage = (value / target) * 100;
  if (percentage >= 90) return 'bg-green-500';
  if (percentage >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getPromotionStatus = (p: Promotion): Promotion['status'] => {
  if (p.status) return p.status;
  if (p.canceled) return 'canceled';
  if (p.expired) return 'expired';
  if (p.active) return 'active';
  if (p.date_paused) return 'paused';
  return 'inactive';
};

interface ServiceRequestFilters {
  status: string;
  type: string;
  organization: string;
  period: string;
}

const ServiceRequestListPage = () => {
  useBreadcrumbs([
    { label: 'Service Requests', href: '#' },
    { label: 'Requests' }
  ]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);

  const {
    debouncedSearchTerm,
    searchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange
  } = useListFilters<ServiceRequestFilters>({
    initialFilters: {
      status: '',
      type: '',
      organization: '',
      period: 'last30days'
    }
  });

  const { data, isLoading } = useServiceRequestsQuery(page, {
    page_size: limit,
    name_contains: debouncedSearchTerm,
    ...getPromotionStatusFilter(filters.status as any),
    // ...getUserRoleFilter(filters.role),
    // ...getUserVerificationFilter(filters.verification),
    // ...getDateRange(filters.activity)
  });

  const service_requests = useMemo(() => data?.data || [],  [data?.data]);

  const handleViewDetails = (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setIsDrawerOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      draft: 'bg-slate-100 text-slate-800',
      expired: 'bg-gray-200 text-gray-800',
      canceled: 'bg-red-100 text-red-800',
      inactive: 'bg-neutral-100 text-neutral-800',
    };
    return styles[status as keyof typeof styles] || styles.inactive;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className=" mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Service Requests</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage and monitor all service requests
              </p>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Create Request
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Requests</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {service_requests.reduce((sum, p) => sum + p.views, 0).toLocaleString()}
                </h3>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <BarChart2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {service_requests.reduce((sum, p) => sum + p.impressions, 0).toLocaleString()}
                </h3>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <ExternalLink className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {(service_requests.reduce((sum, p) => sum + (p.views / (p.impressions || 1)), 0) / (service_requests.length || 1) * 100).toFixed(2)}%
                </h3>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Requets</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {service_requests.filter((p) => getPromotionStatus(p) === 'active').length}
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <ServiceRequestListFilters
          onSearch={handleSearch}
          searchValue={searchTerm}
          onFilterChange={(key, value) => handleFilterChange(key as any, value)}
          filters={filters}
        />

        {/* Promotions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Listing
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading &&
                  !service_requests &&
                  Array.from({ length: limit }).map((_, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <Skeleton className="h-10 w-10 rounded-lg" />
                          </div>
                          <div className="ml-4 space-y-2">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-56" />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-40" />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-3">
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="h-2 w-full" />
                          <Skeleton className="h-2 w-3/4" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          <Skeleton className="h-5 w-20 rounded-full" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-2 w-full" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <Skeleton className="h-4 w-12" />
                          <Skeleton className="h-4 w-10" />
                          <Skeleton className="h-4 w-12" />
                        </div>
                      </td>
                    </tr>
                  ))}

                {!isLoading &&
                  service_requests.map((promotion) => (
                  <tr key={promotion.promid}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500">
                            <Building2 className="h-6 w-6" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{promotion.name?.slice(0, 20) || 'N/A'}</div>
                          <div className="text-sm text-gray-500 truncate max-w-[200px]">{promotion.description_short || promotion.description}</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">OID: {promotion.oid || 'N/A'}</div>
                      <div className="text-sm text-gray-500">Actor UID: {promotion.uid_actor}</div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Views/Impressions</span>
                            <span className="font-medium">
                              {promotion.views.toLocaleString()} / {promotion.impressions.toLocaleString()}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            CTR: {((promotion.views / (promotion.impressions || 1)) * 100).toFixed(2)}%
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Goal Progress</span>
                            <span className="font-medium">
                              {promotion.impressions} / {promotion.impression_target}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`${getProgressColor(
                                promotion.impressions,
                                promotion.impression_target
                              )} h-2 rounded-full`}
                              style={{
                                width: `${Math.min(
                                  (promotion.impressions / (promotion.impression_target || 1)) * 100,
                                  100
                                )}%`
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="mb-2">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(getPromotionStatus(promotion))}`}>
                          {getPromotionStatus(promotion).toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(promotion.date_start).toLocaleDateString()} -{' '}
                        {new Date(promotion.date_expired).toLocaleDateString()}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {/* Budget info missing */}
                        N/A
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        {/* Budget progress missing */}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => handleViewDetails(promotion)}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <Pagination
              total={data?.total ?? 0}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons={true}
              showSelect={true}
              selectOptions={[10, 20, 30, 40, 50]}
            />
          </div>
        </div>
      </div>

      {/* Promotion Details Drawer */}
      <ServiceRequestDetailsDrawer
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        promotion={selectedPromotion}
      />
    </div>
  );
};

export default ServiceRequestListPage;
